import { OakFlex, OakIconName } from "@oaknational/oak-components";
import { ReactNode } from "react";

import { SaveUnitButton } from "../SaveUnitButton/SaveUnitButton";

import {
  getCardListingLinkProps,
  getCardListingTextColour,
  getCardListingBackgroundColour,
  type HighlightColorVariant,
} from "./helpers";
import {
  CardListingStyledLink,
  CardListingTitle,
  CardListingSubCopy,
  CardListingCardTags,
  CardListingLessonCount,
  CardListingChildCardList,
  CardListingIndex,
  CardListingStyledFlex,
} from "./Components";

import { TrackingProgrammeData } from "@/node-lib/educator-api/helpers/saveUnits/utils";

export type CardProps = {
  /** Ignored on parent CardListing when `childCards` is present; use on child cards instead. */
  highlightColorVariant?: HighlightColorVariant;
  title: string;
  lessonCount?: number;
  href: string;
  saveProps?: {
    unitSlug: string;
    unitTitle: string;
    programmeSlug: string;
    trackingProps: TrackingProgrammeData;
  };
  disabled?: boolean;
  onClickLink?: () => void;
  showBorder?: boolean;
};

export type CardListingTags = Array<{ label: string; icon?: OakIconName }>;
export type CardListingProps = CardProps & {
  layoutVariant: "horizontal" | "vertical";
  index?: number;
  subcopy?: ReactNode;
  tags?: CardListingTags;
  childCards?: Array<CardProps>;
};

const CardListing = (props: CardListingProps) => {
  const { layoutVariant, highlightColorVariant, childCards, showBorder } =
    props;

  const hasChildCards = (childCards?.length ?? 0) > 0;

  const backgroundColor = hasChildCards
    ? "bg-primary"
    : getCardListingBackgroundColour(highlightColorVariant);

  return (
    <CardListingStyledFlex
      $borderRadius={"border-radius-m2"}
      $background={backgroundColor}
      $pa={"spacing-20"}
      $gap={"spacing-20"}
      $flexDirection={layoutVariant === "horizontal" ? "row" : "column"}
      $flexGrow={1}
      data-testid="card-listing-container"
      $borderColor={"border-neutral-lighter"}
      $ba={showBorder ? "border-solid-s" : undefined}
      className="card-listing-container"
    >
      <CardListingRenderLayout {...props} />
    </CardListingStyledFlex>
  );
};

const CardListingRenderLayout = (props: CardListingProps) => {
  const {
    layoutVariant,
    highlightColorVariant,
    disabled,
    saveProps,
    href,
    onClickLink,
    childCards,
    lessonCount,
  } = props;

  const hasChildCards = (childCards?.length ?? 0) > 0;
  const showSave = saveProps !== undefined;
  const showFooter = (lessonCount !== undefined || showSave) && !hasChildCards;

  const defaultTextColour = getCardListingTextColour({
    highlightColorVariant,
    disabled,
    hasChildCards,
  });

  const cardLinkProps = getCardListingLinkProps({
    disabled,
    href,
    onClickLink,
  });

  if (hasChildCards) {
    // Modified 'horizontal' layout without card level save or lessonCount
    return (
      <OakFlex $flexDirection={"column"} $gap={"spacing-20"} $width={"100%"}>
        {layoutVariant === "vertical" && <CardListingIndex {...props} />}
        <OakFlex
          $flexDirection={"row"}
          $gap={"spacing-20"}
          $width={"100%"}
          $color={defaultTextColour}
        >
          {layoutVariant === "horizontal" && <CardListingIndex {...props} />}
          <CardListingTitle {...props} />
        </OakFlex>
        <CardListingSubCopy {...props} />
        <CardListingCardTags {...props} />
        <CardListingChildCardList {...props} />
      </OakFlex>
    );
  } else if (layoutVariant === "horizontal") {
    // Horizontal layout with optional save and lesson count
    return (
      <OakFlex $flexDirection={"row"} $gap={"spacing-20"} $width={"100%"}>
        <CardListingStyledLink {...cardLinkProps} className="styled-link">
          <OakFlex
            $flexDirection={["column", "column", "row"]}
            $gap={["spacing-12", "spacing-12", "spacing-20"]}
            $width={"100%"}
            $color={defaultTextColour}
          >
            <CardListingIndex {...props} />
            <OakFlex
              $flexDirection={"column"}
              $gap={"spacing-20"}
              $width={"100%"}
            >
              <CardListingTitle {...props} />
              <CardListingSubCopy {...props} />
              <CardListingCardTags {...props} />
            </OakFlex>
          </OakFlex>
        </CardListingStyledLink>
        {showFooter && (
          <OakFlex
            $alignItems={"center"}
            $alignSelf={"flex-end"}
            $font={"heading-light-7"}
            $gap={"spacing-20"}
          >
            <CardListingLessonCount {...props} />
            {showSave && (
              <SaveUnitButton
                buttonVariant={
                  highlightColorVariant === "secondary" ? "inverted" : "default"
                }
                disabled={disabled}
                {...saveProps}
              />
            )}
          </OakFlex>
        )}
      </OakFlex>
    );
  } else {
    // Vertical layout with optional save and lesson count
    return (
      <OakFlex
        $flexDirection={"column"}
        $gap={"spacing-20"}
        $height={"100%"}
        $justifyContent={"space-between"}
      >
        <CardListingStyledLink {...cardLinkProps}>
          <OakFlex
            $flexDirection={"column"}
            $justifyContent={"space-between"}
            $height="100%"
            $gap={"spacing-20"}
          >
            <OakFlex
              $gap={"spacing-20"}
              $flexDirection={"column"}
              $color={defaultTextColour}
            >
              <CardListingIndex {...props} />
              <CardListingTitle {...props} />
              <CardListingSubCopy {...props} />
            </OakFlex>
            <CardListingCardTags {...props} />
            <CardListingChildCardList {...props} />
          </OakFlex>
        </CardListingStyledLink>
        {showFooter && (
          <OakFlex $justifyContent={"space-between"} $alignItems={"center"}>
            <CardListingLessonCount {...props} />
            {showSave && (
              <SaveUnitButton
                buttonVariant={
                  highlightColorVariant === "secondary" ? "inverted" : "default"
                }
                disabled={disabled}
                {...saveProps}
              />
            )}
          </OakFlex>
        )}
      </OakFlex>
    );
  }
};

export default CardListing;
